import { CSSProperties, FC, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { TopNav } from "@/components";
import { useGenerateAIMessageMutation, useGetAIChatMessagesMutation } from "@/store/usersApi";
import styles from "./AIChatPage.module.scss";
import toast from "react-hot-toast";

export const AIChatPage: FC = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<any[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [generateMessage] = useGenerateAIMessageMutation();
  const [getMessages] = useGetAIChatMessagesMutation();

  // Загружаем историю сообщений
  useEffect(() => {
    const loadMessages = async () => {
      try {
        const result = await getMessages().unwrap();
        setMessages(result || []);
        
        // Находим последний conversation_id из сообщений
        const lastMessage = result?.[result.length - 1];
        if (lastMessage) {
          // Можно извлечь conversation_id из контекста или использовать последний
        }
      } catch (error) {
        console.error("Ошибка загрузки сообщений:", error);
      }
    };

    loadMessages();
  }, [getMessages]);

  // Автопрокрутка к последнему сообщению
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Поддерживаем динамическую высоту viewport на мобильных браузерах,
  // чтобы поле ввода не пряталось за URL-бар/нижнюю панель.
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const updateViewportHeight = () => {
      const nextHeight = window.visualViewport?.height ?? window.innerHeight;
      setViewportHeight(Math.round(nextHeight));
    };

    updateViewportHeight();

    const viewport = window.visualViewport;
    window.addEventListener("resize", updateViewportHeight);
    window.addEventListener("orientationchange", updateViewportHeight);
    viewport?.addEventListener("resize", updateViewportHeight);
    viewport?.addEventListener("scroll", updateViewportHeight);

    return () => {
      window.removeEventListener("resize", updateViewportHeight);
      window.removeEventListener("orientationchange", updateViewportHeight);
      viewport?.removeEventListener("resize", updateViewportHeight);
      viewport?.removeEventListener("scroll", updateViewportHeight);
    };
  }, []);

  const pageStyle = viewportHeight
    ? ({ ["--app-vh" as const]: `${viewportHeight}px` } as CSSProperties)
    : undefined;

  const handleSend = async () => {
    const trimmed = draft.trim();
    if (!trimmed || isSending) {
      return;
    }

    setIsSending(true);
    const userMessage = trimmed;
    setDraft("");

    // Добавляем сообщение пользователя в UI сразу
    const tempUserMessageId = Date.now();
    const tempUserMessage = {
      id: tempUserMessageId,
      author: "user-trainer",
      content: userMessage,
      of_date: new Date().toISOString(),
      chat_id: 0,
      user_id: 0,
    };
    setMessages((prev) => [...prev, tempUserMessage]);

    try {
      // Отправляем запрос на генерацию ответа
      const response = await generateMessage({
        message: userMessage,
        conversation_id: conversationId || undefined,
        context: null,
      }).unwrap();

      // Обновляем conversation_id если он есть
      if (response.conversation_id) {
        setConversationId(response.conversation_id);
      }

      // Перезагружаем сообщения для синхронизации с сервером (включая ответ ИИ)
      const updatedMessages = await getMessages().unwrap();
      setMessages(updatedMessages || []);

      // Показываем уведомление при генерации программы
      if (response.agent === "generate") {
        toast.success("Новая программа тренировок создана!");
      }
    } catch (error: any) {
      console.error("Ошибка отправки сообщения:", error);
      toast.error(error?.data?.detail || "Не удалось отправить сообщение");
      // Удаляем временное сообщение при ошибке
      setMessages((prev) => prev.filter((msg) => msg.id !== tempUserMessageId));
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (
    event
  ) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    } catch (error) {
      return dateString;
    }
  };

  const isOutgoing = (author: string) => author === "trainer-user";

  return (
    <div className={styles.page} style={pageStyle}>
      <div className={styles.header}>
        <TopNav
          title="ИИ-тренер"
          linkPath="/more"
          handleSetActivePage={() => navigate("/more")}
          activePage={0}
        />
      </div>

      <div className={styles.chatWrapper}>
          <div className={styles.messages} ref={scrollRef}>
            {messages.length === 0 && (
              <div className={styles.empty}>
                <div className={styles.emptyIcon}>💬</div>
                <p className={styles.emptyText}>
                  Начните диалог с ИИ-тренером
                </p>
                <p className={styles.emptySubtext}>
                  Задайте вопрос о тренировках, питании или попросите адаптировать программу
                </p>
              </div>
            )}

            {messages.map((message, index) => {
              const outgoing = isOutgoing(message.author);
              const messageKey = message.id || `${message.of_date}-${message.author}-${index}`;
              const content = String(message.content || "");

              return (
                <div
                  key={messageKey}
                  className={`${styles.message} ${
                    outgoing ? styles.outgoing : styles.incoming
                  }`}
                >
                  <div className={styles.bubble}>
                    <div className={styles.content}>
                      {content.split("\n").map((line: string, lineIndex: number) => (
                        <p key={lineIndex}>{line || "\u00A0"}</p>
                      ))}
                    </div>
                    <span className={styles.date}>
                      {formatDate(message.of_date)}
                    </span>
                  </div>
                </div>
              );
            })}

            {isSending && (
              <div className={`${styles.message} ${styles.outgoing}`}>
                <div className={styles.bubble}>
                  <div className={styles.typing}>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={styles.composer}>
            <textarea
              className={styles.textarea}
              placeholder="Напишите сообщение..."
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSending}
              rows={1}
            />
            <button
              type="button"
              className={styles.sendButton}
              onClick={handleSend}
              disabled={isSending || !draft.trim()}
            >
              {isSending ? (
                <span className={styles.spinner}></span>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 2L9 11M18 2L12 18L9 11M18 2L2 8L9 11"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
    </div>
  );
};

